/*
 * PDP gallery: main image with arrows + counter, thumbnail strip with its own arrows.
 * Wraps the vendor <product-slider> (assets/product.js), which keeps lightbox and variant-media behaviour.
 * Markup: sections/main-product.liquid (media_layout: carousel), snippets/kor-media-gallery-thumbs.liquid.
 * Docs: docs/ll-architecture.md.
 */
(function () {
  'use strict';

  if (typeof customElements === 'undefined' || customElements.get('kor-media-gallery')) return;

  class KorMediaGallery extends HTMLElement {
    connectedCallback() {
      // Re-attached after a DOM move: listeners survive, observers were disconnected.
      if (this.slider) {
        this.observe();
        this.sync();
        return;
      }
      this.slider = this.querySelector('product-slider');
      this.track = this.querySelector('[data-thumbs-track]');
      if (!this.slider) return;

      this.prevButton = this.querySelector('[data-gallery-prev]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.currentLabel = this.querySelector('[data-gallery-current]');
      this.totalLabel = this.querySelector('[data-gallery-total]');
      this.thumbsPrev = this.querySelector('[data-thumbs-prev]');
      this.thumbsNext = this.querySelector('[data-thumbs-next]');
      this.status = this.querySelector('[data-gallery-status]');
      const strip = this.querySelector('[data-label-template]');
      this.labelTemplate = strip ? strip.dataset.labelTemplate : '';
      this.slideLabel = this.dataset.slideLabel || '';
      this.index = -1;
      this.frame = 0;
      this.targetIndex = null;
      this.settleTimer = 0;

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.go(this.index - 1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.go(this.index + 1));
      if (this.thumbsPrev) this.thumbsPrev.addEventListener('click', () => this.pageThumbs(-1));
      if (this.thumbsNext) this.thumbsNext.addEventListener('click', () => this.pageThumbs(1));
      if (this.track) {
        this.track.addEventListener('click', this.onThumbClick.bind(this));
        this.track.addEventListener('keydown', this.onThumbKeydown.bind(this));
        this.track.addEventListener('scroll', () => this.schedule(), { passive: true });
      }
      this.querySelector('.kor-gallery__stage').addEventListener('keydown', this.onStageKeydown.bind(this));
      this.slider.addEventListener('scroll', this.onSliderScroll.bind(this), { passive: true });
      this.slider.addEventListener('scrollend', () => this.settle());

      // product.js reorders slides (prepend) and toggles visibility (is-active) on variant / image-set change.
      this.observer = new MutationObserver(() => this.schedule(true));
      // Width changes leave scrollLeft between slides; snap back to the current one.
      this.resizeObserver = new ResizeObserver(() => {
        const slide = this.visibleSlides()[this.index];
        if (slide) this.slider.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
        this.schedule();
      });
      this.observe();

      this.sync();
      this.setAttribute('data-ready', '');
    }

    observe() {
      this.observer.observe(this.slider, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      this.resizeObserver.observe(this.slider);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.settleTimer);
      this.frame = 0;
      this.settleTimer = 0;
      this.targetIndex = null;
    }

    visibleSlides() {
      return Array.from(this.slider.querySelectorAll(':scope > [data-media-id]')).filter((slide) => slide.offsetWidth > 0);
    }

    visibleThumbs() {
      return this.track ? Array.from(this.track.children).filter((thumb) => !thumb.hidden) : [];
    }

    // One update per frame, however many scroll / mutation events arrive.
    schedule(resync) {
      if (resync) this.needsSync = true;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.needsSync) {
          this.needsSync = false;
          this.sync();
        } else {
          this.update();
        }
      });
    }

    // Mirror the slide order and visibility onto the thumbnails, then refresh the UI.
    sync() {
      const slides = this.visibleSlides();
      this.slides = slides;
      if (this.track) {
        const thumbs = new Map(Array.from(this.track.children).map((thumb) => [thumb.dataset.mediaId, thumb]));
        const ordered = slides.map((slide) => thumbs.get(slide.dataset.mediaId)).filter(Boolean);
        const shown = new Set(ordered);
        Array.from(this.track.children).forEach((thumb) => { thumb.hidden = !shown.has(thumb); });
        const visibleNow = this.visibleThumbs();
        const inOrder = visibleNow.length === ordered.length && ordered.every((thumb, i) => visibleNow[i] === thumb);
        if (!inOrder) ordered.forEach((thumb) => this.track.appendChild(thumb));
        ordered.forEach((thumb, i) => {
          if (this.labelTemplate) {
            thumb.setAttribute('aria-label', this.format(this.labelTemplate, i + 1, ordered.length));
          }
        });
      }
      // Slides are vendor markup; label them here so screen readers hear "Image 3 of 8".
      slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        if (this.slideLabel) slide.setAttribute('aria-label', this.format(this.slideLabel, i + 1, slides.length));
      });
      if (this.totalLabel) this.totalLabel.textContent = slides.length;
      this.index = -1;
      this.targetIndex = null;
      this.update();
      // A variant change can prepend a slide whose thumb is scrolled out of the strip.
      const active = this.visibleThumbs()[this.index];
      if (active) this.revealThumb(active);
    }

    format(template, index, count) {
      return template.replace('[index]', index).replace('[count]', count);
    }

    currentIndex() {
      const slides = this.slides || [];
      const left = this.slider.scrollLeft;
      let best = 0;
      slides.forEach((slide, i) => {
        if (Math.abs(slide.offsetLeft - left) < Math.abs(slides[best].offsetLeft - left)) best = i;
      });
      return best;
    }

    // While an arrow / thumbnail scroll is in flight the UI already shows the target, so intermediate
    // scroll positions must not move the counter (1 → 7 would otherwise flick through 2…6).
    update() {
      if (this.targetIndex === null) this.setIndex(this.currentIndex());
      this.updateThumbButtons();
    }

    setIndex(index) {
      if (index === this.index) return;
      const follow = this.index !== -1;
      const slides = this.slides || [];
      this.index = index;
      if (this.currentLabel) this.currentLabel.textContent = index + 1;
      if (follow && this.status && this.slideLabel) this.status.textContent = this.format(this.slideLabel, index + 1, slides.length);
      // An arrow hides itself at the ends; hand focus to the other one so keyboard users are not dropped to <body>.
      const atStart = index <= 0;
      const atEnd = index >= slides.length - 1;
      if (atStart && document.activeElement === this.prevButton && !atEnd) this.nextButton.focus();
      if (atEnd && document.activeElement === this.nextButton && !atStart) this.prevButton.focus();
      if (this.prevButton) this.prevButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atEnd;
      const thumbs = this.visibleThumbs();
      thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (follow && thumbs[index]) this.revealThumb(thumbs[index]);
    }

    go(index) {
      const slides = this.slides || [];
      const clamped = Math.max(0, Math.min(index, slides.length - 1));
      const target = slides[clamped];
      if (!target) return;
      this.targetIndex = clamped;
      this.setIndex(clamped);
      this.slider.scrollTo({ left: target.offsetLeft });
      this.armSettle();
    }

    onSliderScroll() {
      if (this.targetIndex !== null) this.armSettle();
      this.schedule();
    }

    // scrollend where supported; otherwise (and as a safety net) 150ms without scroll events.
    armSettle() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 150);
    }

    settle() {
      clearTimeout(this.settleTimer);
      if (this.targetIndex === null) return;
      this.targetIndex = null;
      this.schedule();
    }

    // Keep the active thumbnail fully inside the strip, moving the strip as little as possible.
    // The track is position: relative, so offsetLeft is measured from its scroll origin.
    revealThumb(thumb) {
      const start = thumb.offsetLeft;
      const end = start + thumb.offsetWidth;
      const viewStart = this.track.scrollLeft;
      const viewEnd = viewStart + this.track.clientWidth;
      if (start < viewStart) this.track.scrollTo({ left: start });
      else if (end > viewEnd) this.track.scrollTo({ left: end - this.track.clientWidth });
    }

    // Page by whole thumbnails, keeping one from the previous view for context. Targets a real thumb's
    // offsetLeft (thumb widths are fractional, so multiplying a rounded pitch would drift).
    pageThumbs(direction) {
      const thumbs = this.visibleThumbs();
      if (thumbs.length < 2) return;
      const pitch = thumbs[1].offsetLeft - thumbs[0].offsetLeft;
      const perView = Math.max(1, Math.round((this.track.clientWidth + pitch - thumbs[0].offsetWidth) / pitch));
      const first = Math.round(this.track.scrollLeft / pitch);
      const target = Math.max(0, Math.min(thumbs.length - 1, first + direction * Math.max(1, perView - 1)));
      this.track.scrollTo({ left: thumbs[target].offsetLeft });
    }

    updateThumbButtons() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.thumbsPrev) this.thumbsPrev.disabled = this.track.scrollLeft <= 1;
      if (this.thumbsNext) this.thumbsNext.disabled = this.track.scrollLeft >= max - 1;
    }

    onThumbClick(event) {
      const thumb = event.target.closest('.kor-gallery__thumb');
      if (!thumb || !this.track.contains(thumb)) return;
      this.go(this.visibleThumbs().indexOf(thumb));
    }

    // Arrow keys move focus along the strip; Enter / Space (native button) shows the image.
    onThumbKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const thumbs = this.visibleThumbs();
      const i = thumbs.indexOf(event.target.closest('.kor-gallery__thumb'));
      if (i === -1) return;
      const next = thumbs[i + (event.key === 'ArrowRight' ? 1 : -1)];
      if (next) {
        event.preventDefault();
        next.focus();
        this.revealThumb(next);
      }
    }

    onStageKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      // Leave arrow keys to media that use them (3D orbit, video seek, embedded players).
      if (event.target.closest('product-model, model-viewer, video, iframe, input, textarea, select, [contenteditable]')) return;
      event.preventDefault();
      this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
    }
  }

  customElements.define('kor-media-gallery', KorMediaGallery);
})();
